import AppConstants from "AppConstants";
import * as yup from "yup";

const phoneRegExp = /^(?:(?:\+|00)([1-9][0-9]))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})$/;
const numericRegExp = /^[0-9]+$/;

const mobileNumberValidation = yup
  .string()
  .matches(phoneRegExp, "Phone number is not valid")
  .matches(numericRegExp, "Only numbers are allowed")
  .required("Mobile number is required");

export const OnboardingFormSchema = yup.object().shape({
  firstName: yup
    .string()
    .matches(/^[A-Za-z ]+$/, "Name must contain only letters")
    .min(2)
    .required("First name is required"),
  middleName: yup.string().matches(/^[A-Za-z ]+$/, "Name must contain only letters"),
  lastName: yup
    .string()
    .matches(/^[A-Za-z ]+$/, "Name must contain only letters")
    .min(3)
    .required("Last name is required"),
  pancard: yup
    .string()
    .test("", "Invalid pan number", function (value) {
      return AppConstants.panCardRegex.test(value);
    })
    .required("Pan Number is required"),
  aadharcard: yup
    .string()
    .test("", "Invalid Aadhar number", function (value) {
      return AppConstants.aadharregex.test(value);
    })
    .required("Aadhar Number is required"),
  dobDocument: yup
    .date()
    .nullable()
    .test("is-adult", "Age must be at least 18 years old to register", function (value) {
      if (!value) return true;
      const dob = new Date(value);
      const eighteenYearsAgo = new Date();
      eighteenYearsAgo.setFullYear(eighteenYearsAgo.getFullYear() - 18);
      return dob <= eighteenYearsAgo;
    })
    .required("Date of birth (document) is required"),
  dobActual: yup
    .date()
    .test("is-adult", "Age must be at least 18 years old to register", function (value) {
      if (!value) return true;
      const dob = new Date(value);
      const eighteenYearsAgo = new Date();
      eighteenYearsAgo.setFullYear(eighteenYearsAgo.getFullYear() - 18);
      return dob <= eighteenYearsAgo;
    })
    .required("Actual date of birth is required"),
  gender: yup.string().required(),
  aadharcardfile: yup.string().required("Aadhar file is required"),
  pancardfile: yup.string().required("Pan File is required"),
  email: yup
    .string()
    .email("Invalid email format")
    .test("strongEmail", "Invalid email format", function (value) {
      const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;
      if (!value) {
        return true; // If the value is empty, the 'required' validation will handle it
      }
      return emailRegex.test(value);
    })
    .required("Email is required"),
  mobileNum: mobileNumberValidation,
  sec_mobileNum: mobileNumberValidation.test(
    "notSameAsMobileNum",
    "Secondary mobile number must be different",
    function (value) {
      const mobileNum = this.parent.mobileNum;
      return value !== mobileNum;
    }
  ),
  family_member_first_name: yup
    .string()
    .matches(/^[A-Za-z ]+$/, "Name must contain only letters")
    .min(2)
    .required("Family member first name is required"),
  family_member_last_name: yup
    .string()
    .matches(/^[A-Za-z ]+$/, "Name must contain only letters")
    .min(2)
    .required("Family member last name is required"),
  relationship: yup.string().required("select relationship is required"),
  family_member_dob: yup
    .date()
    .nullable()
    .test("is-adult", "Age must be at least 18 years old to register", function (value) {
      if (!value) return true;
      const dob = new Date(value);
      const eighteenYearsAgo = new Date();
      eighteenYearsAgo.setFullYear(eighteenYearsAgo.getFullYear() - 18);
      return dob <= eighteenYearsAgo;
    })
    .required("Date of birth is required"),
  family_member_phone: mobileNumberValidation,
  family_member_email: yup
    .string()
    .email("Invalid email format")
    .test("strongEmail", "Invalid email format", function (value) {
      const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;
      if (!value) {
        return true; // If the value is empty, the 'required' validation will handle it
      }
      return emailRegex.test(value);
    }),
});

export const AddressFormSchema = yup.object().shape({
  c_aLine1: yup.string().required("Current address line 1 is required"),
  c_aState: yup.string().required("Current address state is required"),
  c_country: yup.string().required("Current address Country is required"),
  c_aCity: yup.string().required("Current address city is required"),
  c_aZip: yup
    .string()
    .matches(/^\d{6}$/, "Current address ZIP code must be 6 digits")
    .required("Current address ZIP code is required"),
  p_aLine1: yup.string().required("Permanent address line 1 is required"),
  p_aState: yup.string().required("Permanent address state is required"),
  p_country: yup.string().required("Permanent address Country is required"),
  p_aCity: yup.string().required("Permanent address city is required"),
  p_aZip: yup
    .string()
    .matches(/^\d{6}$/, "Permanent address ZIP code must be 6 digits")
    .required("Permanent address ZIP code is required"),
  // terms: yup.bool().required().oneOf([true], "Terms must be accepted"),
});

const MAX_FILE_SIZE = 1024 * 1024 * 10;
const VALID_FILE_EXTENSIONS = {
  image: ["jpg", "gif", "png", "jpeg", "svg", "webp"],
  document: ["doc", "docx", "pdf"],
};

function isValidFileType(value) {
  if (!value || !value.name) return false;
  const fileExtension = value.name.split(".").pop().toLowerCase();
  return VALID_FILE_EXTENSIONS.image.includes(fileExtension) || VALID_FILE_EXTENSIONS.document.includes(fileExtension);
}
function isFileSizeValid(value) {
  return value && value.size <= MAX_FILE_SIZE;
}

export const EducationFormSchema = yup.object().shape({
  secondaryPer: yup
    .string()
    .trim()
    .required("Secondary percentage is required")
    .matches(/^\d+(\.\d{1,2})?$/, "Invalid percentage format")
    .test("is-valid-percentage", "Percentage must be between 33 and 100", (value) => {
      if (value) {
        const percentage = parseFloat(value);
        return percentage >= 33 && percentage <= 100;
      }
      return true;
    })
    .test("no-leading-zeros", "Leading zeros are not allowed", (value) => /^([1-9]\d*|0)(\.\d{1,2})?$/.test(value)),

  secondaryResult: yup
    .mixed()
    .test("file-type", "Invalid file type", function (value) {
      if (value && typeof value !== "string") {
        return isValidFileType(value);
      }
      return true;
    })
    .test("file-size", "File size is too large", function (value) {
      if (value && typeof value !== "string") {
        return isValidFileType(value) && isFileSizeValid(value);
      }
      return true;
    })
    .required("Secondary result file is required"),

  seniorPer: yup
    .string()
    .trim()
    .required("Senior percentage is required")
    .matches(/^\d+(\.\d{1,2})?$/, "Invalid percentage format")
    .test("is-valid-percentage", "Percentage must be between 33 and 100", (value) => {
      if (value) {
        const percentage = parseFloat(value);
        return percentage >= 33 && percentage <= 100;
      }
      return true;
    })
    .test("no-leading-zeros", "Leading zeros are not allowed", (value) => /^([1-9]\d*|0)(\.\d{1,2})?$/.test(value)),

  seniorResult: yup
    .mixed()
    .test("file-type", "Invalid file type", function (value) {
      if (value && typeof value !== "string") {
        return isValidFileType(value);
      }
      return true;
    })
    .test("file-size", "File size is too large", function (value) {
      if (value && typeof value !== "string") {
        return isValidFileType(value) && isFileSizeValid(value);
      }
      return true;
    })
    .required("Senior result is required"),

  bachelorsPer: yup
    .string()
    .trim()
    .required("Bachelors percentage is required")
    .matches(/^\d+(\.\d{1,2})?$/, "Invalid percentage format")
    .test("is-valid-percentage", "Percentage must be between 33 and 100", (value) => {
      if (value) {
        const percentage = parseFloat(value);
        return percentage >= 33 && percentage <= 100;
      }
      return true;
    })
    .test("no-leading-zeros", "Leading zeros are not allowed", (value) => /^([1-9]\d*|0)(\.\d{1,2})?$/.test(value)),

  bachelorsResult: yup
    .mixed()
    .test("file-type", "Invalid file type", function (value) {
      if (value && typeof value !== "string") {
        return isValidFileType(value);
      }
      return true;
    })
    .test("file-size", "File size is too large", function (value) {
      if (value && typeof value !== "string") {
        return isValidFileType(value) && isFileSizeValid(value);
      }
      return true;
    })
    .required("Bachelors result is required"),
});

export const documentFormSchema = yup.object().shape({
  companyname: yup.string().required("Company name is required"),
  start_date: yup.date().required("Start date is required"),
  end_date: yup.date().required("End date is required"),
  offerletter: yup
    .mixed()
    .test("file-type", "Invalid file type", function (value) {
      if (value && typeof value !== "string") {
        return isValidFileType(value);
      }
      return true;
    })
    .test("file-size", "File size is too large", function (value) {
      if (value && typeof value !== "string") {
        return isValidFileType(value) && isFileSizeValid(value);
      }
      return true;
    })
    .required("Offer letter is required"),
  relievingletter: yup
    .mixed()
    .test("file-type", "Invalid file type", function (value) {
      if (value && typeof value !== "string") {
        return isValidFileType(value);
      }
      return true;
    })
    .test("file-size", "File size is too large", function (value) {
      if (value && typeof value !== "string") {
        return isValidFileType(value) && isFileSizeValid(value);
      }
      return true;
    })
    .required("Relieving letter is required"),
  resignationletter: yup
    .mixed()
    .test("file-type", "Invalid file type", function (value) {
      if (value && typeof value !== "string") {
        return isValidFileType(value);
      }
      return true;
    })
    .test("file-size", "File size is too large", function (value) {
      if (value && typeof value !== "string") {
        return isValidFileType(value) && isFileSizeValid(value);
      }
      return true;
    })
    .required("Resignation letter is required"),
  compensation: yup
    .mixed()
    .test("file-type", "Invalid file type", function (value) {
      if (value && typeof value !== "string") {
        return isValidFileType(value);
      }
      return true;
    })
    .test("file-size", "File size is too large", function (value) {
      if (value && typeof value !== "string") {
        return isValidFileType(value) && isFileSizeValid(value);
      }
      return true;
    })
    .required("Compensation is required"),
  experienceletter: yup
    .mixed()
    .test("file-type", "Invalid file type", function (value) {
      if (value && typeof value !== "string") {
        return isValidFileType(value);
      }
      return true;
    })
    .test("file-size", "File size is too large", function (value) {
      if (value && typeof value !== "string") {
        return isValidFileType(value) && isFileSizeValid(value);
      }
      return true;
    })
    .required("Experience letter is required"),

  payslip: yup
    .mixed()
    .test("file-type", "Invalid file type", function (value) {
      if (value && typeof value !== "string") {
        return isValidFileType(value);
      }
      return true;
    })
    .test("file-size", "File size is too large", function (value) {
      if (value && typeof value !== "string") {
        return isValidFileType(value) && isFileSizeValid(value);
      }
      return true;
    })
    .required("Payslip is required"),

  resignationletter: yup
    .mixed()
    .test("file-type", "Invalid file type", function (value) {
      if (value && typeof value !== "string") {
        return isValidFileType(value);
      }
      return true;
    })
    .test("file-size", "File size is too large", function (value) {
      if (value && typeof value !== "string") {
        return isValidFileType(value) && isFileSizeValid(value);
      }
      return true;
    })
    .required("resignation letter is required"),
  terms: false,
});

export const loginSchema = yup.object({
  email: yup.string().email("Invalid email address").required("Email address is required *"),
  password: yup.string().required("Password is required *").min(5, "Your password is too short."),
  // .matches(/[a-zA-Z]/, "Password can only contain Latin letters."),
});

export const NewPasswordSchema = yup.object({
  password: yup
    .string()
    .required("Password is required")
    .min(8, "Your password is too short.")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/,
      "Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and one special case Character"
    ),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password")], "Passwords must match")
    .required("Confirm Password is required"),
});

export const AssatSchema = yup.object({
  assetName: yup.string().trim().required("Asset Name is required").max(25, "Asset Name is too long"),
  purchaseDate: yup.date().required("Purchase Date is required").nullable(),
  manufacturer: yup.string().trim().required("Manufacturer is required").max(50, "Manufacturer is too long"),
  serialNumber: yup.string().trim().required("Serial Number is required").max(50, "Serial Number is too long"),
  assetValue: yup.number().required("Asset Value is required").positive("Asset Value must be a positive number"),
  documentUpload: yup
    .mixed()
    .test("file-type", "Invalid file type", function (value) {
      if (value && typeof value !== "string") {
        return isValidFileType(value);
      }
      return true;
    })
    .test("file-size", "File size is too large", function (value) {
      if (value && typeof value !== "string") {
        return isValidFileType(value) && isFileSizeValid(value);
      }
      return true;
    }),
  assetId: yup.string().trim().required("Asset ID is required").max(50, "Asset ID is too long"),
  purchase: yup.string().required("Purchase from is required"),
  model: yup.string().trim().required("Model is required").max(50, "Model is too long"),
  supplier: yup.string().trim().required("Supplier is required").max(50, "Supplier is too long"),
  warranty: yup
    .date()
    .required("Warranty is required")
    .nullable()
    .test("warranty-test", "Warranty date must be after Purchase date", function (value) {
      const { purchaseDate } = this.parent;
      return purchaseDate && value ? value > purchaseDate : true;
    }),
  description: yup.string().trim().max(1000, "Description is too long"),
  assetStatus: yup.string().trim().required("Status is required").max(50, "Status is too long"),
});

export const EditSalarySchema = yup.object().shape({
  // employee_name: yup.string().required("Employee name is required"),
  // department: yup.string().required("Department is required"),
  // pan_number: yup.string().required("PAN number is required"),
  // bank_account_number: yup.string().required("Bank account number is required"),
  // uan: yup.string().required("UAN is required"),
  // present_day: yup.number().required("Present day is required"),
  // joining_date: yup.date().required("Joining date is required"),
  // paid_leave: yup.number().required("Paid leave is required"),
  // basic_salary: yup.number().required("Basic salary is required"),
  // transport_allowance: yup.number().required("Transport allowance is required"),
  // total_earning: yup.number().required("Total earning is required"),
  // tds: yup.number().required("TDS is required"),
  // miscellaneous: yup.number().required("Miscellaneous is required"),
  // pf: yup.number().required("PF is required"),
  // employeeId: yup.string().required("Employee ID is required"),
  // designation: yup.string().required("Designation is required"),
  // pay_mode: yup.string().required("Pay mode is required"),
  // pf_number: yup.string().required("PF number is required"),
  // gross_salary: yup.number().required("Gross salary is required"),
  // total_leave: yup.number().required("Total leave is required"),
  // unpaid_leave: yup.number().required("Unpaid leave is required"),
  // hra: yup.number().required("HRA is required"),
  // bonus: yup.number(),
  // net_salary: yup.number().required("Net salary is required"),
  // leave_deduction: yup.number().required("Leave deduction is required"),
  // advance_salary: yup.number().required("Advance salary is required"),
  // esic: yup.number().required("ESIC is required"),
  // total_deductions: yup.number(),
  // description: yup.string(),
});
